// ─── useNovaAINavigation ──────────────────────────────────────────────────────
// Isolated hook for handling Nova AI navigation intents.
// Called from NovaAIChatWindow when the API response contains a navigation action.
// No existing navigation or screen code is modified — this is Nova AI-only logic.

import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '@shared/store';
import {setActiveTab} from '@shared/store/menuSlice';
import {
  requestFlowExit,
  setNovaAIRepairNavigation,
  triggerHomeReload,
  triggerRepairsReload,
} from '@shared/store/novaAISlice';
import {
  setOpenCreateWorkOrder,
  setOpenRepairFlow,
  setRepairTabRequest,
  setSelectedRepairId,
} from '@shared/store/repairSlice';
import {clearSelectedTechnicianData} from '@shared/store/technicianSlice';
import {closeQuickAssistScreen} from '@shared/store/quickAssistSlice';
import {navigationRef} from '@shared/navigation/navigationRef';
import {intentionalNavFlag} from '@shared/navigation/intentionalNavFlag';
import {menuTabs} from '@features/layout/screens/MainLayoutScreen/constants/mainLayout.constant';
import {repairNavParamsStore} from '@features/repairDetails/repairNavParamsStore';
import {NovaNavigationAction} from './NovaAIProps';
import {userIdStorage} from '@features/auth/api/userIdStorage';
import {signoutWithBrowser} from '@features/auth/services/azureCiamService';

// ── Path → left-side menu tab ─────────────────────────────────────────────────
const PATH_TO_MENU_TAB: Record<string, string> = {
  repairs: menuTabs.repairs,
  home: menuTabs.home,
  parts: menuTabs.parts,
};

// ── Path → the flow MainLayoutScreen opens ───────────────────────────────────
// Lowercased, because `handleNavigation` lowercases the path it is given.
const PATH_TO_FLOW: Record<string, 'workOrder' | 'repair'> = {
  'create work order': 'workOrder',
  'create repair': 'repair',
};

// ── API scope string → repairFilterOptions value ──────────────────────────────
const SCOPE_TO_FILTER_VALUE: Record<string, string> = {
  my_repairs_my_shop: '0',    // My Repairs / My Shop
  my_repairs_all_shops: '1',  // My Repairs / All Shops
  all_repairs_my_shop: '2',   // All Repairs / My Shop
  all_repairs_all_shops: '3', // All Repairs / All Shops
};

export function useNovaAINavigation() {
  const dispatch = useDispatch();
  const selectedRepairId = useSelector(
    (state: RootState) => state.repair.selectedRepairId,
  );
  const activeFlowScreen = useSelector(
    (state: RootState) => state.menu.activeFlowScreen,
  );
  const isFlowProtected = useSelector(
    (state: RootState) => state.repair.isFlowProtected,
  );

  /**
   * Performs the logout sequence mirroring Header.tsx logout() with showProfile=true:
   *  1. Clear technician data and reset active tab
   *  2. Set intentional nav flag so beforeRemove handlers don't intercept
   *  3. Non-Admin → clear userId storage, sign out from Azure CIAM, go to AgencySelectionScreen
   *     Admin     → go to ShopHomePage
   */
  const handleLogout = async () => {
    const role = userIdStorage.getRole();
    dispatch(closeQuickAssistScreen());
    dispatch(clearSelectedTechnicianData());
    dispatch(setActiveTab(menuTabs.home));
    intentionalNavFlag.current = true;

    // if (role !== 'Admin') {
    //   userIdStorage.clear();
    //   await signoutWithBrowser();
    //   navigationRef.current?.reset({
    //     index: 0,
    //     routes: [{name: 'AgencySelectionScreen' as never}],
    //   });
    // } else {
      navigationRef.current?.reset({
        index: 0,
        routes: [{name: 'ShopHomePage' as never}],
      });
    //}
  };

  /**
   * Processes a Nova AI navigation action:
   *  1. If Path is "Logout" — perform full logout sequence
   *  2. Otherwise sets the active left-side tab via Redux
   *  3. Stores repair filter intent in novaAISlice (consumed by WorkOrdersScreen)
   *  4. Navigates to the target screen
   */
  const handleNavigation = (navAction: NovaNavigationAction) => {
    const tabPath = (navAction.Path?.[0] ?? '').toLowerCase();

    // ── Logout path ───────────────────────────────────────────────────────────
    if (tabPath === 'logout') {
      handleLogout();
      return;
    }

    // ── Flow paths ────────────────────────────────────────────────────────────
    // Nova asks for a wizard rather than a tab. MainLayoutScreen watches these
    // two flags and opens it. Without a branch here the path fell through to
    // the `?? menuTabs.home` default below, so "create a new work order" landed
    // on Home — reported by Kasi, 13 Sep 2026.
    const flowFlag = PATH_TO_FLOW[tabPath];
    if (flowFlag) {
      // The wizard renders over the main layout and its breadcrumb reads
      // "Dashboard > …", so Home is the tab underneath it. Any open repair is
      // cleared for the same reason it is on a filtered navigation: the layout
      // renders the detail screen while one is selected.
      dispatch(setActiveTab(menuTabs.home));
      dispatch(setSelectedRepairId(''));
      dispatch(flowFlag === 'workOrder' ? setOpenCreateWorkOrder(true) : setOpenRepairFlow(true));
      return;
    }

    const menuTab = PATH_TO_MENU_TAB[tabPath] ?? menuTabs.home;

    // A create flow open on screen is left the way a left-menu tap leaves it,
    // or the tab changes underneath the wizard and the technician stays on it:
    // "start new repair" on the New Repair screen said "Navigating to your
    // repairs list" and went nowhere. On a protected step nothing is lost
    // unasked — the same "leave this flow?" the menu shows, going to this tab
    // on a yes. Reported by Kasi, 30 Sep 2026.
    if (activeFlowScreen) {
      if (isFlowProtected) {
        dispatch(requestFlowExit(menuTab));
        return;
      }
      dispatch(requestFlowExit(null));
    }

    // 1. Switch the left-side menu tab
    dispatch(setActiveTab(menuTab));

    if (navAction.filters?.repairId) {
      // 2a. Repair detail navigation — set the selected repair ID so that
      //     MainLayoutScreen renders <RepairDetailsScreen /> instead of
      //     <WorkOrdersScreen /> (selectedRepairId truthy triggers the switch).
      //
      //     The repair's code goes where a card tap puts it, just before the
      //     tap selects the repair: the detail screen's "Repair Code", and the
      //     PM/SA check on beginning it, read the code from
      //     repairNavParamsStore, because GET WorkOrderRepairs/{id} returns it
      //     as null. Nova sends it when it picked the repair off a list row.
      //     Without one — a repair named by its id — a different repair must
      //     not show the code of the one on screen, so the store is cleared;
      //     the repair already open keeps the code its card set. Opened
      //     through Nova it read "N/A-N/A-N/A". Reported by Kasi, 30 Sep 2026.
      const {repairId, actionID, groupID, componentID} = navAction.filters;
      if (actionID || groupID || componentID) {
        repairNavParamsStore.set({
          actionID: actionID ?? '',
          groupID: groupID ?? '',
          componentID: componentID ?? '',
          // Nothing reads the numeric ids; a card without them sends 0 too.
          repairActionID: 0,
          repairGroupID: 0,
          repairComponentID: 0,
        });
      } else if (String(repairId) !== String(selectedRepairId ?? '')) {
        repairNavParamsStore.clear();
      }
      dispatch(setSelectedRepairId(repairId));
      // One of the repair's own tabs, when Nova named one — "switch to work
      // order notes". RepairDetailsScreen shows the tab, the Notes or
      // Attachments view its own tab. Without it the screen keeps its tab.
      const {repairTab, repairTabSection} = navAction.filters;
      if (repairTab) {
        dispatch(
          setRepairTabRequest({
            tab: repairTab,
            ...(repairTabSection ? {section: repairTabSection} : {}),
          }),
        );
      }
    } else if (!navAction.filters) {
      // 2c. A plain navigation with no filters at all — "go to my repairs".
      // The open repair has to be cleared here too, or MainLayoutScreen keeps
      // rendering <RepairDetailsScreen /> while selectedRepairId is truthy:
      // the tab changed underneath and the screen did not, so asking from a
      // repair went nowhere. Reported by Kasi, 22 Sep 2026.
      dispatch(setSelectedRepairId(''));
    } else {
      // 2b. Navigation with filters — store intent for the target screen.
      // Clear any open repair detail so MainLayoutScreen renders the list screen.
      dispatch(setSelectedRepairId(''));
      // Which of Home's two sections this is about. Nova sends the section
      // name in filters.tab for the Home path ("Repairs" / "Parts Inventory");
      // anything that is not Parts Inventory is the repairs section, including
      // the case where no tab was sent at all.
      const isDashboardRepairsSection =
        tabPath === 'home' &&
        (navAction.filters.tab ?? '').toLowerCase() !== 'parts inventory';
      // Only when Nova actually named a scope. Coercing an absent one to '0'
      // meant every repair action reset the Shop Scope dropdown to My Repairs /
      // My Shop — including a plain status switch, which is exactly what Nova
      // omits the scope to avoid: '0' is a non-empty string, so it passed the
      // truthiness guards below and won over the screen's own filter. undefined
      // leaves the technician's scope where they set it.
      const scopeValue = navAction.filters.scope
        ? SCOPE_TO_FILTER_VALUE[navAction.filters.scope] ?? '0'
        : undefined;
      // "parts messages" is not one of the Parts sub-tabs — it is the messages
      // view behind the screen's own Parts Messages button. Nova sends it as
      // filters.tab on the Parts path, so it is routed to its own field and
      // kept out of partsInventoryTabFilter, which PartsInventory matches
      // against its tab list.
      const isPartsMessages =
        tabPath === 'parts' &&
        (navAction.filters.tab ?? '').toLowerCase() === 'parts messages';
      dispatch(
        setNovaAIRepairNavigation({
          // For Home path, filters.tab is a dashboard section name
          // (e.g. "parts inventory", "repairs") — stored as dashboardTabFilter.
          // For Parts path, filters.tab is a parts sub-tab
          // (e.g. "active requests") — stored as partsInventoryTabFilter.
          // For Repairs path (or any other), it is a repair status filter
          // (e.g. "Open", "All", "Complete") — stored as statusFilter.
          // On a Home path filters.tab names the SECTION, so the status travels
          // in filters.status and is routed by that section. Without this a
          // filter asked for on Home arrived with the status already dropped.
          // Traced 30 Sep 2026: "status":"Requested" in, gone by redux.
          statusFilter:
            tabPath === 'home'
              ? (isDashboardRepairsSection ? navAction.filters.status : undefined)
              : tabPath !== 'parts'
                ? navAction.filters.tab
                : undefined,
          dashboardTabFilter: tabPath === 'home' ? navAction.filters.tab : undefined,
          partsInventoryTabFilter:
            tabPath === 'parts' && !isPartsMessages ? navAction.filters.tab : undefined,
          // The tab Nova named — the tab strip's own value (All / UnRead /
          // Read). It falls back to UnRead, which is the screen's own default.
          partsMessagesFilter: isPartsMessages
            ? (navAction.filters.messages ?? 'UnRead')
            : undefined,
          scopeFilter: scopeValue,
          // Pass raw sort label (e.g. "HIGH") — matched against API-driven
          // priorityDropdownOptions in RepairsSection by label comparison.
          sortFilter: navAction.filters.sort,
          // "List" → list view, "Card" / "Tile" → tile view.
          //
          // Home carries two lists, so the path alone does not say which one
          // the view belongs to — the section in `filters.tab` does. Parts
          // Inventory needs the separate field because DashboardScreen clears
          // its own fields with clearDashboardNavigation, letting the value
          // survive until PartsInventory mounts; the dashboard's repairs
          // section is already mounted and reads plain `viewFilter`, so
          // routing its view through the parts field left it undefined and the
          // switch silently did nothing.
          viewFilter:
            tabPath !== 'home' || isDashboardRepairsSection
              ? navAction.filters.view
              : undefined,
          partsInventoryViewFilter:
            tabPath === 'home' && !isDashboardRepairsSection
              ? navAction.filters.view
              : undefined,
          // 1-based page number for the Repairs list (used by WorkOrdersScreen /
          // DashboardScreen). Not forwarded on the Parts path (which has its own
          // partsInventoryPageFilter).
          pageFilter: tabPath !== 'parts' ? navAction.filters.page : undefined,
          // 1-based page number for Parts Inventory pagination.
          // Only forwarded on the Parts path; ignored for Home / Repairs.
          partsInventoryPageFilter: tabPath === 'parts' ? navAction.filters.page : undefined,
          // Status label for the Parts Inventory filter dropdown (e.g. "Reserved").
          // Matched case-insensitively against allStatuses[].label in PartsInventory.
          partsInventoryStatusFilter:
            tabPath === 'parts' || (tabPath === 'home' && !isDashboardRepairsSection)
              ? navAction.filters.status
              : undefined,
          // Search category key (e.g. "license") → pre-selects the search-category
          // dropdown in RepairsSection. Forwarded on Repairs and Home (dashboard) paths.
          searchTextFilter: (tabPath === 'repairs' || tabPath === '' || tabPath === 'home') ? navAction.filters.search_text : undefined,
          // Search value (e.g. "ABC1234") → pre-fills the search input.
          searchValueFilter: (tabPath === 'repairs' || tabPath === '' || tabPath === 'home') ? navAction.filters.search_value : undefined,
        }),
      );
    }

    // 3. Navigate to the target screen (currently only MainLayoutScreen supported)
    if (navAction.ScreenName === 'MainLayoutScreen') {
      navigationRef.current?.navigate('MainLayoutScreen' as never);
    }

    // 4. If the API flags Reload:true the user is already on this screen —
    //    signal it to re-fetch in place. Home and the Repairs list listen on
    //    different signals, and only Home's was sent: answering "yes" to
    //    "want me to show this on the repairs screen?" while standing on that
    //    list sent the filters it already had and nothing moved. Reported by
    //    Kasi, 22 Sep 2026.
    if (navAction.Reload) {
      if (tabPath === 'repairs') {
        dispatch(triggerRepairsReload(undefined));
      } else {
        dispatch(triggerHomeReload());
      }
    }
  };

  return {handleNavigation};
}